import { createTheme } from '@mui/material/styles';
import createBreakpoints from '@mui/system/createTheme/createBreakpoints';

// import './Global.scss';

/* SUMMARY:
  -Material UI is our default style library, use API/Component docs provided by MUI to generate layouts
  -A custom theme is generated to allow for global over-rides of MUI
  -Styled-components are used to further customize each component
  -A Global.scss file is included as a last-resort when MUI or React doesn't cooperate
  Hierarchy:
    StylesProvider                                     //Allows for inline styles to override MUI
    > Material UI Default                              //Defines all default styling settings, variables, props, usage
      > Custom MUI Theme (theme)                       //Global overrides for MUI settings with an exposed custom set of variables
        > Styled-Components (design components)        //Inherits both the MUI and custom theme - universal design component override
          > Styled-Components (react components)       //Inherits all settings from above - specific/unique component override
            > SCSS Import                              //Force style overrides globally - the WTF option
*/
// @ts-ignore
declare module '@mui/material/styles/createMuiTheme' {
  interface Theme {
    drawer: {
      width: React.CSSProperties['width'];
      widthMobile: React.CSSProperties['width'];
    };
    nav: {
      width: string;
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
    layout: {
      toolBarHeight: {
        xs: number;
        sm: number;
        md: number;
      };
    };
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    drawer?: {
      width?: React.CSSProperties['width'];
      widthMobile?: React.CSSProperties['width'];
    };
    nav?: {
      width: string;
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };

    layout?: {
      toolBarHeight?: {
        xs: number;
        sm: number;
        md: number;
      };
      lightBackground?: string;
    };
  }
}

// TODO: we may want to update the breakpoints ourselves, https://stackoverflow.com/questions/68554470/was-the-createbreakpoints-method-removed-in-material-version-5
const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
});
/* explicitly create a custom theme, this is shared down to styled-components */

const themeObject = {
  palette: {
    primary: { main: '#10254d' },
    secondary: { main: '#0b91d0' },
    // temporary color change for secondary, want to change the V2 action blue button color, but dont want to change dashboard sections yet (need a new secondaryV2 color name)

    warning: {
      main: '#ff9800', //Default MUI
      light: '#ffdc5c',
      lightest: '#fff6d2',
    },
    success: {
      main: '#4caf50', //Default MUI
      light: '#9bef9e',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
          lineHeight: 1.43,
          letterSpacing: '0.01071em',
          backgroundColor: '#f6fcff',
          overscrollBehavior: 'none',
        },
        [breakpoints.down('sm')]: {
          body: {
            backgroundColor: '#fff',
          },
        },
      },
    },
  },

  drawer: {
    width: 384,
    widthMobile: 304,
  },
  nav: {
    width: '240px',
    xs: '300px',
    sm: '300px',
    md: '240px',
    lg: '240px',
    xl: '240px',
    [breakpoints.up('xs')]: {
      width: '300px',
    },
    [breakpoints.up('sm')]: {
      width: '300px',
    },
    [breakpoints.up('md')]: {
      width: '240px',
    },
    [breakpoints.up('lg')]: {
      width: '240px',
    },
    [breakpoints.up('xl')]: {
      width: '240px',
    },
  },
  layout: {
    toolBarHeight: {
      xs: 56,
      sm: 56,
      md: 56,
    },
    lightBackground: '#f6fcff',
  },
};

const theme = createTheme({
  ...themeObject,
});

export { theme };
